import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  alpha,
  useTheme,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Chip
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import RoadIcon from '@mui/icons-material/Timeline';
import SensorsIcon from '@mui/icons-material/Sensors';
import SearchIcon from '@mui/icons-material/Search';
import WarningIcon from '@mui/icons-material/Warning';
// Import user context for loading state
import { useAuth } from '../../../context/AuthContext';

// Placeholder data structures - these will be passed as props
// const organizationsList = [{ id: 1, name: 'Org A' }];
// const networksList = [{ id: 1, name: 'Network 1' }];
// const sectionsList = [{ id: 1, name: 'Section X' }];
// const categoriesList = [{ id: 1, name: 'Category 1' }];
// const subtypesList = [{ id: 2, name: 'Subtype A' }];

// Menu props for multi-select dropdowns
const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 5.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const FilterPanel = ({
  // Filter options data (arrays of objects with id, name)
  organizations = [],
  networks = [],
  sections = [],
  categories = [],
  types = [],
  subtypes = [],
  regions = [], // Dynamic regions from sections, not hardcoded
  ratingLevels = [], // Rating levels for selected asset type
  // System admin flag
  isSystemAdmin = false,
  // Currently selected filter values
  selectedOrganizationId = '',
  selectedNetworkIds = [], // Changed to array
  selectedSectionIds = [], // Changed to array
  selectedRegions = [], // Changed to array
  selectedCategoryId = '',
  selectedTypeId = '',
  selectedSubtypeId = '',
  selectedStatus = '',
  selectedRatingLevelId = '', // New rating filter
  selectedSlopeType = '', // Slope type filter (C=Cut, E=Embankment)
  selectedIsCritical = '', // Critical asset filter
  // Callback functions to update state in parent
  onOrganizationChange,
  onNetworkChange,
  onSectionChange,
  onRegionChange,
  onCategoryChange,
  onTypeChange,
  onSubtypeChange,
  onStatusChange,
  onRatingChange, // New rating change handler
  onSlopeTypeChange, // Slope type change handler
  onIsCriticalChange, // Critical asset filter handler
  onResetFilters,
  // Counts for display
  displayedCount = 0,
  totalCount = null,
  // Layer visibility controls
  showRoadLines = true,
  setShowRoadLines,
  showIoTSensors = true,
  setShowIoTSensors,
  // Search term props
  searchTerm = '',
  onSearchTermChange,
  assets = []
}) => {
  const theme = useTheme();
  // Get user context for loading state only (isSystemAdmin is now passed as prop from FilterManager)
  const { isLoading } = useAuth();

  const handleFilterChange = (event, handler) => {
    if (handler) {
      handler(event.target.value);
    }
  };

  // Multi-select handler
  const handleMultiSelectChange = (event, handler) => {
    if (handler) {
      const value = event.target.value;
      handler(typeof value === 'string' ? value.split(',') : value);
    }
  };

  // Show loading state if authentication is still loading
  if (isLoading) {
    return (
      <Paper
        elevation={6}
        sx={{
          position: 'absolute',
          top: 70,
          left: 16,
          zIndex: 10,
          padding: 2,
          backgroundColor: alpha(theme.palette.background.paper, 0.95),
          borderRadius: 2.5,
          minWidth: '340px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100px'
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Loading authentication...
        </Typography>
      </Paper>
    );
  }

  // Asset status options
  const statusOptions = [
    { value: 'under_construction', label: 'Under Construction' },
    { value: 'under_renovation', label: 'Under Renovation' },
    { value: 'active', label: 'Active' },
    { value: 'maintenance', label: 'Maintenance' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'decommissioned', label: 'Decommissioned' }
  ];

  // For non-admins, selectedOrganizationId is auto-set by FilterManager
  // For admins, they can select any organization
  const effectiveOrganizationId = selectedOrganizationId;

  return (
    <Paper
      elevation={6}
      sx={{
        position: 'absolute',
        top: 70, // Position below the filter button
        left: 16, // Align with the filter button
        zIndex: 10,
        padding: 2,
        backgroundColor: alpha(theme.palette.background.paper, 0.95), // Slightly more opaque
        backdropFilter: 'blur(10px)',
        borderRadius: 2.5, // Slightly more rounded corners
        minWidth: '340px', // Slightly wider
        width: 'fit-content',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.2, // Slightly more spacing between elements
        maxHeight: 'calc(100vh - 120px)',
        overflowY: 'auto',
        boxShadow: '0 10px 30px rgba(0,0,0,0.12), 0 4px 8px rgba(0,0,0,0.06)',
        animation: 'fadeIn 0.3s ease-in-out',
        '@keyframes fadeIn': {
          '0%': { opacity: 0, transform: 'translateY(-20px)' },
          '100%': { opacity: 1, transform: 'translateY(0)' }
        }
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <FilterListIcon color="primary" sx={{ mr: 1, fontSize: '1.2rem' }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: '1rem', color: theme.palette.primary.main }}>
            Filter Assets
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title={showRoadLines ? "Hide Highway Network" : "Show Highway Network"}>
            <IconButton 
              size="small" 
              onClick={() => setShowRoadLines(!showRoadLines)}
              sx={{ 
                color: showRoadLines ? 'success.main' : alpha(theme.palette.text.secondary, 0.5),
                bgcolor: showRoadLines ? alpha(theme.palette.success.main, 0.1) : 'transparent',
                border: showRoadLines ? `1px solid ${alpha(theme.palette.success.main, 0.2)}` : `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                '&:hover': {
                  bgcolor: showRoadLines 
                    ? alpha(theme.palette.success.main, 0.15) 
                    : alpha(theme.palette.action.hover, 0.1)
                }
              }}
            >
              <RoadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={showIoTSensors ? "Hide IoT Sensors" : "Show IoT Sensors"}>
            <IconButton 
              size="small" 
              onClick={() => setShowIoTSensors(!showIoTSensors)}
              sx={{ 
                color: showIoTSensors ? 'error.main' : alpha(theme.palette.text.secondary, 0.5),
                bgcolor: showIoTSensors ? alpha(theme.palette.error.main, 0.1) : 'transparent',
                border: showIoTSensors ? `1px solid ${alpha(theme.palette.error.main, 0.2)}` : `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                '&:hover': {
                  bgcolor: showIoTSensors 
                    ? alpha(theme.palette.error.main, 0.15) 
                    : alpha(theme.palette.action.hover, 0.1)
                }
              }}
            >
              <SensorsIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {/* Removed 3D Model button */}
          {/* Remove Asset Footprints button */}
          {/* Removed Asset Points Toggle Button */}
        </Box>
      </Box>

      {/* Search Input - Enhanced with icon and styling */}
      <FormControl fullWidth variant="outlined" sx={{ mb: 1.5 }}>
        <TextField
          placeholder="Search Assets"
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => onSearchTermChange(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" sx={{ fontSize: '1.2rem' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              fontSize: '0.9rem',
              backgroundColor: alpha(theme.palette.background.default, 0.6),
              transition: 'all 0.2s',
              '&:hover': {
                backgroundColor: alpha(theme.palette.background.default, 0.8),
                boxShadow: '0 2px 6px rgba(0,0,0,0.05)'
              },
              '&.Mui-focused': {
                backgroundColor: alpha(theme.palette.background.paper, 0.95),
                boxShadow: '0 3px 8px rgba(0,0,0,0.08)'
              }
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: alpha(theme.palette.primary.main, 0.2),
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: alpha(theme.palette.primary.main, 0.5),
            },
          }}
        />
      </FormControl>

      {/* Location Filter Group */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        gap: 0.8,
        backgroundColor: alpha(theme.palette.background.default, 0.3),
        padding: 1.2,
        borderRadius: 2,
        boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.05)'
      }}>
        <Typography 
          variant="caption" 
          sx={{ 
            fontWeight: 600, 
            color: alpha(theme.palette.text.primary, 0.7),
            mb: 0.5,
            fontSize: '0.75rem',
            letterSpacing: '0.03em',
            textTransform: 'uppercase'
          }}
        >
          Location Filters
        </Typography>

        {/* Organization Filter - Only visible to System Admins */}
        {isSystemAdmin && (
          <FormControl fullWidth size="small" variant="outlined" sx={{ mb: 0.5 }}>
            <InputLabel id="org-filter-label" sx={{ fontSize: '0.85rem' }}>
              Organization
            </InputLabel>
            <Select
              labelId="org-filter-label"
              label="Organization"
              value={effectiveOrganizationId || ''}
              onChange={(e) => handleFilterChange(e, onOrganizationChange)}
              sx={{
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: alpha(theme.palette.text.primary, 0.15),
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: alpha(theme.palette.primary.main, 0.5),
                },
                borderRadius: 1.5,
                fontSize: '0.85rem'
              }}
            >
              <MenuItem value="" sx={{ fontSize: '0.85rem' }}>
                <em>All Organizations</em>
              </MenuItem>
              {organizations.map((org) => (
                <MenuItem key={org.id} value={org.id} sx={{ fontSize: '0.85rem' }}>
                  {org.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Network and Region Filters - Side by Side with Multi-Select */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          {/* Network Filter - Multi-Select */}
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', gap: 0.5, mb: 0.3, justifyContent: 'flex-end' }}>
              <Button
                size="small"
                variant="text"
                onClick={() => onNetworkChange(networks.map(n => n.id))}
                disabled={!effectiveOrganizationId || networks.length === 0}
                sx={{ fontSize: '0.65rem', minWidth: 'auto', p: '2px 6px', textTransform: 'none' }}
              >
                All
              </Button>
              <Button
                size="small"
                variant="text"
                onClick={() => onNetworkChange([])}
                disabled={!effectiveOrganizationId || selectedNetworkIds.length === 0}
                sx={{ fontSize: '0.65rem', minWidth: 'auto', p: '2px 6px', textTransform: 'none' }}
              >
                Clear
              </Button>
            </Box>
            <FormControl size="small" variant="outlined" fullWidth>
              <InputLabel id="network-filter-label" sx={{ fontSize: '0.85rem' }}>
                Network
              </InputLabel>
              <Select
                labelId="network-filter-label"
                multiple
                value={selectedNetworkIds}
                onChange={(e) => handleMultiSelectChange(e, onNetworkChange)}
                input={<OutlinedInput label="Network" />}
                disabled={!effectiveOrganizationId}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.3 }}>
                    {selected.length <= 2 ? (
                      selected.map((id) => {
                        const net = networks.find(n => n.id === id);
                        return (
                          <Chip
                            key={id}
                            label={net?.name || id}
                            size="small"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        );
                      })
                    ) : (
                      <Chip
                        label={`${selected.length} selected`}
                        size="small"
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                    )}
                  </Box>
                )}
                MenuProps={MenuProps}
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: alpha(theme.palette.text.primary, 0.15),
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: alpha(theme.palette.primary.main, 0.5),
                  },
                  borderRadius: 1.5,
                  fontSize: '0.85rem'
                }}
              >
                {networks.map((net) => (
                  <MenuItem key={net.id} value={net.id} sx={{ fontSize: '0.85rem', py: 0.5 }}>
                    <Checkbox checked={selectedNetworkIds.includes(net.id)} size="small" />
                    <ListItemText primary={net.name} primaryTypographyProps={{ fontSize: '0.85rem' }} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Region Filter - Multi-Select */}
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', gap: 0.5, mb: 0.3, justifyContent: 'flex-end' }}>
              <Button
                size="small"
                variant="text"
                onClick={() => onRegionChange(regions)}
                disabled={!effectiveOrganizationId || regions.length === 0}
                sx={{ fontSize: '0.65rem', minWidth: 'auto', p: '2px 6px', textTransform: 'none' }}
              >
                All
              </Button>
              <Button
                size="small"
                variant="text"
                onClick={() => onRegionChange([])}
                disabled={!effectiveOrganizationId || selectedRegions.length === 0}
                sx={{ fontSize: '0.65rem', minWidth: 'auto', p: '2px 6px', textTransform: 'none' }}
              >
                Clear
              </Button>
            </Box>
            <FormControl size="small" variant="outlined" fullWidth>
              <InputLabel id="region-filter-label" sx={{ fontSize: '0.85rem' }}>
                Region
              </InputLabel>
              <Select
                labelId="region-filter-label"
                multiple
                value={selectedRegions}
                onChange={(e) => handleMultiSelectChange(e, onRegionChange)}
                input={<OutlinedInput label="Region" />}
                disabled={!effectiveOrganizationId}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.3 }}>
                    {selected.length <= 2 ? (
                      selected.map((region) => (
                        <Chip
                          key={region}
                          label={region}
                          size="small"
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      ))
                    ) : (
                      <Chip
                        label={`${selected.length} selected`}
                        size="small"
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                    )}
                  </Box>
                )}
                MenuProps={MenuProps}
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: alpha(theme.palette.text.primary, 0.15),
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: alpha(theme.palette.primary.main, 0.5),
                  },
                  borderRadius: 1.5,
                  fontSize: '0.85rem'
                }}
              >
                {regions.map((region) => (
                  <MenuItem key={region} value={region} sx={{ fontSize: '0.85rem', py: 0.5 }}>
                    <Checkbox checked={selectedRegions.includes(region)} size="small" />
                    <ListItemText primary={region} primaryTypographyProps={{ fontSize: '0.85rem' }} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>

        {/* Section Filter - Multi-Select */}
        <Box>
          <Box sx={{ display: 'flex', gap: 0.5, mb: 0.3, justifyContent: 'flex-end' }}>
            <Button
              size="small"
              variant="text"
              onClick={() => onSectionChange(sections.map(s => s.id))}
              disabled={!effectiveOrganizationId || sections.length === 0}
              sx={{ fontSize: '0.65rem', minWidth: 'auto', p: '2px 6px', textTransform: 'none' }}
            >
              All
            </Button>
            <Button
              size="small"
              variant="text"
              onClick={() => onSectionChange([])}
              disabled={!effectiveOrganizationId || selectedSectionIds.length === 0}
              sx={{ fontSize: '0.65rem', minWidth: 'auto', p: '2px 6px', textTransform: 'none' }}
            >
              Clear
            </Button>
          </Box>
          <FormControl fullWidth size="small" variant="outlined" sx={{ mb: 0.3 }}>
            <InputLabel id="section-filter-label" sx={{ fontSize: '0.85rem' }}>Section</InputLabel>
            <Select
              labelId="section-filter-label"
              multiple
              value={selectedSectionIds}
              onChange={(e) => handleMultiSelectChange(e, onSectionChange)}
              input={<OutlinedInput label="Section" />}
              disabled={!effectiveOrganizationId}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.3 }}>
                  {selected.length <= 2 ? (
                    selected.map((id) => {
                      const sec = sections.find(s => s.id === id);
                      return (
                        <Chip
                          key={id}
                          label={sec?.name || id}
                          size="small"
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      );
                    })
                  ) : (
                    <Chip
                      label={`${selected.length} selected`}
                      size="small"
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                  )}
                </Box>
              )}
              MenuProps={MenuProps}
              sx={{
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: alpha(theme.palette.text.primary, 0.15),
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: alpha(theme.palette.primary.main, 0.5),
                },
                borderRadius: 1.5,
                fontSize: '0.85rem'
              }}
            >
              {sections.map((sec) => (
                <MenuItem key={sec.id} value={sec.id} sx={{ fontSize: '0.85rem', py: 0.5 }}>
                  <Checkbox checked={selectedSectionIds.includes(sec.id)} size="small" />
                  <ListItemText primary={sec.name} primaryTypographyProps={{ fontSize: '0.85rem' }} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* Asset Type Filter Group */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        gap: 0.8,
        backgroundColor: alpha(theme.palette.background.default, 0.3),
        padding: 1.2,
        borderRadius: 2,
        boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.05)',
        mt: 0.5
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
          <Typography 
            variant="caption" 
            sx={{ 
              fontWeight: 600, 
              color: alpha(theme.palette.text.primary, 0.7),
              fontSize: '0.75rem',
              letterSpacing: '0.03em',
              textTransform: 'uppercase'
            }}
          >
            Asset Type Filters
          </Typography>
          
          {/* Status filter - subtle dropdown */}
          <FormControl size="small" sx={{ minWidth: '120px' }}>
            <Select
              value={selectedStatus}
              onChange={(e) => handleFilterChange(e, onStatusChange)}
              displayEmpty
              variant="standard"
              sx={{
                fontSize: '0.75rem',
                '&:before, &:after': { display: 'none' },
                '& .MuiSelect-select': { 
                  paddingY: 0.3,
                  paddingX: 0.5,
                  color: selectedStatus ? theme.palette.primary.main : alpha(theme.palette.text.secondary, 0.7),
                  fontWeight: selectedStatus ? 500 : 400,
                  bgcolor: selectedStatus ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                  borderRadius: 1,
                  '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.12)
                  }
                }
              }}
              IconComponent={(props) => (
                <Box component="svg" 
                  viewBox="0 0 24 24" 
                  sx={{ 
                    width: 16, 
                    height: 16, 
                    color: alpha(theme.palette.text.primary, 0.5), 
                    ml: -0.5, 
                    ...props.sx 
                  }}
                >
                  <path d="M7 10l5 5 5-5z" fill="currentColor"/>
                </Box>
              )}
            >
              <MenuItem value="" sx={{ fontSize: '0.75rem' }}>
                <em>Status: Any</em>
              </MenuItem>
              {statusOptions.map((option) => (
                <MenuItem 
                  key={option.value} 
                  value={option.value}
                  sx={{ fontSize: '0.75rem' }}
                >
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Critical Filter */}
          <FormControl size="small" sx={{ minWidth: '120px' }}>
            <Select
              value={selectedIsCritical}
              onChange={(e) => handleFilterChange(e, onIsCriticalChange)}
              displayEmpty
              variant="standard"
              sx={{
                fontSize: '0.75rem',
                '&:before, &:after': { display: 'none' },
                '& .MuiSelect-select': {
                  paddingY: 0.3,
                  paddingX: 0.5,
                  color: selectedIsCritical === 'true' ? '#d32f2f' :
                         selectedIsCritical ? theme.palette.primary.main : alpha(theme.palette.text.secondary, 0.7),
                  fontWeight: selectedIsCritical ? 500 : 400,
                  bgcolor: selectedIsCritical === 'true' ? alpha('#d32f2f', 0.08) :
                           selectedIsCritical ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  '&:hover': {
                    bgcolor: selectedIsCritical === 'true' ? alpha('#d32f2f', 0.12) :
                             alpha(theme.palette.primary.main, 0.12)
                  }
                }
              }}
              IconComponent={(props) => (
                <Box component="svg"
                  viewBox="0 0 24 24"
                  sx={{
                    width: 16,
                    height: 16,
                    color: alpha(theme.palette.text.primary, 0.5),
                    ml: -0.5,
                    ...props.sx
                  }}
                >
                  <path d="M7 10l5 5 5-5z" fill="currentColor"/>
                </Box>
              )}
              renderValue={(value) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {value === 'true' && <WarningIcon sx={{ fontSize: '0.9rem', color: '#d32f2f' }} />}
                  {value === '' ? <em>Critical: Any</em> :
                   value === 'true' ? 'Critical Only' : 'Non-Critical'}
                </Box>
              )}
            >
              <MenuItem value="" sx={{ fontSize: '0.75rem' }}>
                <em>Critical: Any</em>
              </MenuItem>
              <MenuItem value="true" sx={{ fontSize: '0.75rem' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <WarningIcon sx={{ fontSize: '0.9rem', color: '#d32f2f' }} />
                  Critical Only
                </Box>
              </MenuItem>
              <MenuItem value="false" sx={{ fontSize: '0.75rem' }}>
                Non-Critical
              </MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Category Filter */}
        <FormControl fullWidth size="small" variant="outlined" sx={{ mb: 0.5 }}>
          <InputLabel id="category-filter-label" sx={{ fontSize: '0.85rem' }}>Category</InputLabel>
          <Select
            labelId="category-filter-label"
            label="Category"
            value={selectedCategoryId}
            onChange={(e) => handleFilterChange(e, onCategoryChange)}
            sx={{
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: alpha(theme.palette.text.primary, 0.15),
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: alpha(theme.palette.primary.main, 0.5),
              },
              borderRadius: 1.5,
              fontSize: '0.85rem'
            }}
          >
            <MenuItem value="" sx={{ fontSize: '0.85rem' }}><em>All Categories</em></MenuItem>
            {categories.map((cat) => (
              <MenuItem key={cat.id} value={cat.id} sx={{ fontSize: '0.85rem' }}>{cat.name}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Type Filter */}
        <FormControl fullWidth size="small" variant="outlined" sx={{ mb: 0.5 }}>
          <InputLabel id="type-filter-label" sx={{ fontSize: '0.85rem' }}>Type</InputLabel>
          <Select
            labelId="type-filter-label"
            label="Type"
            value={selectedTypeId}
            onChange={(e) => handleFilterChange(e, onTypeChange)}
            disabled={!selectedCategoryId}
            sx={{
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: alpha(theme.palette.text.primary, 0.15),
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: alpha(theme.palette.primary.main, 0.5),
              },
              borderRadius: 1.5,
              fontSize: '0.85rem'
            }}
          >
            <MenuItem value="" sx={{ fontSize: '0.85rem' }}><em>All Types</em></MenuItem>
            {types.map((type) => (
              <MenuItem key={type.id} value={type.id} sx={{ fontSize: '0.85rem' }}>{type.name}</MenuItem>
            ))}
          </Select>
          {!selectedCategoryId && (
            <Typography 
              variant="caption" 
              color="text.secondary" 
              sx={{ mt: 0.2, ml: 1, fontSize: '0.65rem', fontStyle: 'italic', opacity: 0.7 }}
            >
              Select a category first
            </Typography>
          )}
        </FormControl>

        {/* Subtype Filter */}
        <FormControl fullWidth size="small" variant="outlined" sx={{ mb: 0.3 }}>
          <InputLabel id="subtype-filter-label" sx={{ fontSize: '0.85rem' }}>Subtype</InputLabel>
          <Select
            labelId="subtype-filter-label"
            label="Subtype"
            value={selectedSubtypeId}
            onChange={(e) => handleFilterChange(e, onSubtypeChange)}
            disabled={!selectedTypeId}
            sx={{
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: alpha(theme.palette.text.primary, 0.15),
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: alpha(theme.palette.primary.main, 0.5),
              },
              borderRadius: 1.5,
              fontSize: '0.85rem'
            }}
          >
            <MenuItem value="" sx={{ fontSize: '0.85rem' }}><em>All Subtypes</em></MenuItem>
            {subtypes.map((sub) => (
              <MenuItem key={sub.id} value={sub.id} sx={{ fontSize: '0.85rem' }}>{sub.name}</MenuItem>
            ))}
          </Select>
          {!selectedTypeId && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 0.2, ml: 1, fontSize: '0.65rem', fontStyle: 'italic', opacity: 0.7 }}
            >
              Select a type first
            </Typography>
          )}
        </FormControl>

        {/* Slope Type Filter - Only visible when Slope type (id: 4) is selected */}
        {selectedTypeId === 4 || selectedTypeId === '4' ? (
          <FormControl fullWidth size="small" variant="outlined" sx={{ mb: 0.3 }}>
            <InputLabel id="slope-type-filter-label" sx={{ fontSize: '0.85rem' }}>Slope Type</InputLabel>
            <Select
              labelId="slope-type-filter-label"
              label="Slope Type"
              value={selectedSlopeType}
              onChange={(e) => handleFilterChange(e, onSlopeTypeChange)}
              sx={{
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: alpha(theme.palette.text.primary, 0.15),
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: alpha(theme.palette.primary.main, 0.5),
                },
                borderRadius: 1.5,
                fontSize: '0.85rem'
              }}
            >
              <MenuItem value="" sx={{ fontSize: '0.85rem' }}><em>All Slope Types</em></MenuItem>
              <MenuItem value="C" sx={{ fontSize: '0.85rem' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    bgcolor: '#8bc34a'
                  }} />
                  Cut
                </Box>
              </MenuItem>
              <MenuItem value="E" sx={{ fontSize: '0.85rem' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    bgcolor: '#ff9800'
                  }} />
                  Embankment
                </Box>
              </MenuItem>
            </Select>
          </FormControl>
        ) : null}

        {/* Hazard & Risk Ranking Filter */}
        <FormControl fullWidth size="small" variant="outlined" sx={{ mb: 0.3 }}>
          <InputLabel id="rating-filter-label" sx={{ fontSize: '0.85rem' }}>Hazard & Risk Ranking</InputLabel>
          <Select
            labelId="rating-filter-label"
            label="Hazard & Risk Ranking"
            value={selectedRatingLevelId}
            onChange={(e) => handleFilterChange(e, onRatingChange)}
            disabled={ratingLevels.length === 0}
            sx={{
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: alpha(theme.palette.text.primary, 0.15),
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: alpha(theme.palette.primary.main, 0.5),
              },
              borderRadius: 1.5,
              fontSize: '0.85rem'
            }}
          >
            <MenuItem value="" sx={{ fontSize: '0.85rem' }}><em>All Ratings</em></MenuItem>
            <MenuItem value="null" sx={{ fontSize: '0.85rem' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#9e9e9e' }} />
                Not Rated
              </Box>
            </MenuItem>
            {ratingLevels.map((level) => (
              <MenuItem key={level.id} value={level.id} sx={{ fontSize: '0.85rem' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    bgcolor: level.color_code || '#9e9e9e'
                  }} />
                  {level.name.replace('Cx', 'Rx')}
                </Box>
              </MenuItem>
            ))}
          </Select>
          {ratingLevels.length === 0 && selectedTypeId && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 0.2, ml: 1, fontSize: '0.65rem', fontStyle: 'italic', opacity: 0.7 }}
            >
              No rating scheme for this type
            </Typography>
          )}
          {!selectedTypeId && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 0.2, ml: 1, fontSize: '0.65rem', fontStyle: 'italic', opacity: 0.7 }}
            >
              Select a type first
            </Typography>
          )}
        </FormControl>
      </Box>

      {/* Reset Button */}
      <Button
        variant="outlined"
        startIcon={<RestartAltIcon sx={{ fontSize: '1rem' }} />}
        onClick={onResetFilters}
        sx={{ 
          mt: 1.5,
          borderRadius: 2,
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '0.85rem',
          py: 0.7,
          color: theme.palette.primary.main,
          borderColor: alpha(theme.palette.primary.main, 0.5),
          boxShadow: '0 2px 4px rgba(0,0,0,0.04)',
          transition: 'all 0.2s',
          '&:hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.05),
            borderColor: theme.palette.primary.main,
            boxShadow: '0 4px 6px rgba(0,0,0,0.08)',
            transform: 'translateY(-1px)'
          }
        }}
      >
        Reset Filters
      </Button>

      {/* Display Count Status */}
      <Box 
        sx={{ 
          mt: 1.5,
          py: 0.9, 
          px: 1.5, 
          bgcolor: alpha(theme.palette.primary.main, 0.08),
          borderRadius: 2,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.03)'
        }}
      >
        <Typography 
          variant="body2" 
          sx={{ 
            color: theme.palette.primary.main,
            fontWeight: 500,
            fontSize: '0.8rem'
          }}
        >
          {totalCount !== null
            ? `Displaying ${displayedCount} of ${totalCount} assets`
            : `Displaying ${displayedCount} assets`}
        </Typography>
      </Box>
    </Paper>
  );
};

export default FilterPanel;